import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, type AllandoLista, type AllandoSor, type Nap } from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { szam } from '../formazas.ts';
import { EtteremKereso } from '../reszek/EtteremKereso.tsx';
import { Mezo, SzamMezo, ValasztoMezo } from '../reszek/Mezo.tsx';
import { Varolista } from '../reszek/Varolista.tsx';

const NAP_SORREND: Nap[] = ['H', 'K', 'SZ', 'CS', 'P'];

/**
 * Állandó címek — napokra bontva. (Megbízás 6.8)
 *
 * Amit itt rögzítesz, azt a járatszervezés egy gombbal behúzza. A mennyiségek
 * itt **javaslatok**: a járatlapra másolódnak, és ott már önálló életet élnek,
 * így egy állandó cím utólagos átírása nem változtatja meg a múltat.
 */
export function AllandoCimek() {
  const alapadatok = useAlapadatok();
  const [nap, setNap] = useState<Nap>('H');
  const [lista, setLista] = useState<AllandoLista | null>(null);
  const [toltes, setToltes] = useState(true);
  const [hiba, setHiba] = useState<string | null>(null);
  const [varolistaNyitva, setVarolistaNyitva] = useState(false);
  const [varoDarab, setVaroDarab] = useState<number | null>(null);

  const betolt = useCallback(async () => {
    setToltes(true);
    try {
      setLista(await api.get<AllandoLista>(`/api/allando?nap=${nap}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    } finally {
      setToltes(false);
    }
  }, [nap]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const ment = (id: number, mezo: string) => async (ertek: string) => {
    const frissitett = await api.patch<AllandoSor>(`/api/allando/${id}/mezo/${mezo}`, { ertek });
    setLista((elozo) =>
      elozo
        ? { ...elozo, sorok: elozo.sorok.map((s) => (s.id === id ? frissitett : s)) }
        : elozo,
    );
  };

  async function torol(sor: AllandoSor) {
    if (!confirm(`Törlöd a(z) ${sor.etterem_nev} állandó címét erről a napról?`)) return;
    try {
      await api.torol(`/api/allando/${sor.id}`);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  const fizetesiLehetosegek = useMemo(
    () =>
      alapadatok.fizetesi_modok
        .filter((m) => m.kod !== 'KP')
        .map((m) => ({ ertek: m.kod, cimke: m.nev })),
    [alapadatok.fizetesi_modok],
  );

  const gyakorisagLehetosegek = useMemo(
    () =>
      Object.entries(lista?.gyakorisag_nevek ?? {}).map(([ertek, cimke]) => ({
        ertek,
        cimke: String(cimke),
      })),
    [lista?.gyakorisag_nevek],
  );

  const sorok = lista?.sorok ?? [];

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Állandó címek</span>
        <span className="hint">
          Amit itt rögzítesz, azt a járatszervezés egy gombbal behúzza.
        </span>
        <div className="stats">
          <div>
            Bejegyzés
            <strong>{lista?.osszes ?? 0}</strong>
          </div>
        </div>
      </div>

      <div className="drv">
        {NAP_SORREND.map((n) => (
          <button key={n} className={nap === n ? 'on' : ''} onClick={() => setNap(n)}>
            {lista?.nap_nevek[n] ?? n}
            <span className="cnt">{lista?.nap_darabok[n] ?? 0}</span>
          </button>
        ))}
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}

      {/* A régi Excelből még be nem hozott, döntésre váró sorok */}
      <Varolista
        nyitva={varolistaNyitva}
        onNyit={setVarolistaNyitva}
        onDarab={setVaroDarab}
        darab={varoDarab}
        onValtozott={() => void betolt()}
      />

      <div className="split">
        <EtteremKereso
          fizetesiLehetosegek={fizetesiLehetosegek}
          gombCimke="Állandó mentése"
          onValaszt={async (e, ertekek) => {
            await api.post('/api/allando', {
              etterem_id: e.id,
              nap,
              mo: ertekek.mo,
              fo: ertekek.fo,
              mennyiseg: ertekek.mennyiseg,
              fizetesi_mod: ertekek.fizetesi_mod || null,
            });
            await betolt();
          }}
        />

        <div>
          <div className="sheet">
            <table>
              <thead>
                <tr>
                  <th className="n">#</th>
                  <th>Étterem</th>
                  <th>Cím</th>
                  <th className="n g">MO</th>
                  <th className="n">FO</th>
                  <th className="n">Mennyiség</th>
                  <th className="g">Fizetés</th>
                  <th>Gyakoriság</th>
                  <th>Megjegyzés</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorok.map((s, i) => (
                  <tr key={s.id} className={s.etterem_aktiv ? undefined : 'nul'}>
                    <td className="idx">{i + 1}</td>
                    <td className="nm">
                      <b>{s.etterem_nev}</b>
                      {!s.etterem_aktiv && <small className="only"> — nem aktív</small>}
                    </td>
                    <td className="addr">
                      {[s.etterem_irsz, s.etterem_cim].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="q g">
                      <SzamMezo
                        ertek={s.mo}
                        onMent={ment(s.id, 'mo')}
                        helykitolto="0"
                        osztaly="c"
                      />
                    </td>
                    <td className="q">
                      <SzamMezo
                        ertek={s.fo}
                        onMent={ment(s.id, 'fo')}
                        helykitolto="0"
                        osztaly="c"
                      />
                    </td>
                    <td className="q">
                      <SzamMezo
                        ertek={s.mennyiseg}
                        onMent={ment(s.id, 'mennyiseg')}
                        helykitolto="0"
                        osztaly="c"
                      />
                    </td>
                    <td className="sel g">
                      <ValasztoMezo
                        ertek={s.fizetesi_mod}
                        uresCimke="Készpénz"
                        lehetosegek={fizetesiLehetosegek}
                        onMent={ment(s.id, 'fizetesi_mod')}
                      />
                    </td>
                    <td className="sel">
                      <ValasztoMezo
                        ertek={s.gyakorisag}
                        kotelezo
                        lehetosegek={gyakorisagLehetosegek}
                        onMent={ment(s.id, 'gyakorisag')}
                      />
                    </td>
                    <td className="sel">
                      <Mezo ertek={s.megjegyzes} onMent={ment(s.id, 'megjegyzes')} osztaly="" />
                    </td>
                    <td className="act">
                      <button className="xb" title="Törlés" onClick={() => void torol(s)}>
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {sorok.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={3} className="dcol">
                      {sorok.length} cím ezen a napon
                    </td>
                    <td className="n">{szam(osszeg(sorok, 'mo'), 0)}</td>
                    <td className="n">{szam(osszeg(sorok, 'fo'), 0)}</td>
                    <td className="n">{szam(osszeg(sorok, 'mennyiseg'), 0)}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {sorok.length === 0 && (
            <div className="blank">
              {toltes ? 'Betöltés…' : 'Erre a napra még nincs állandó cím.'}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function osszeg(sorok: AllandoSor[], mezo: 'mo' | 'fo' | 'mennyiseg'): number {
  return sorok.reduce((a, s) => a + (s[mezo] ?? 0), 0);
}
